import React, { useEffect, useMemo, useRef, useState } from 'react';

const normalizeRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => ({
    localId: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    student_email: (row.student_email || '').trim(),
    student_identifier: (row.student_identifier || '').trim(),
  }));
};

const RosterEditorModal = ({
  isOpen,
  onClose,
  moduleName,
  year,
  initialRows,
  onSave,
  isSaving,
}) => {
  const [rows, setRows] = useState([]);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setRows(normalizeRows(initialRows));
    }
  }, [isOpen, initialRows]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousActive = document.activeElement;
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!isSaving) onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousActive && typeof previousActive.focus === 'function') {
        previousActive.focus();
      }
    };
  }, [isOpen, isSaving, onClose]);

  const duplicateEmails = useMemo(() => {
    const seen = new Map();
    const duplicates = new Set();

    rows.forEach((row) => {
      const key = (row.student_email || '').trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) {
        duplicates.add(key);
      } else {
        seen.set(key, true);
      }
    });

    return duplicates;
  }, [rows]);

  if (!isOpen) return null;

  const updateRow = (localId, field, value) => {
    setRows((prev) => prev.map((row) => (
      row.localId === localId ? { ...row, [field]: value } : row
    )));
  };

  const addRow = () => {
    setRows((prev) => ([
      ...prev,
      {
        localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        student_email: '',
        student_identifier: '',
      },
    ]));
  };

  const deleteRow = (localId) => {
    setRows((prev) => prev.filter((row) => row.localId !== localId));
  };

  const handleSave = () => {
    const trimmedRows = rows.map((row) => ({
      student_email: (row.student_email || '').trim(),
      student_identifier: (row.student_identifier || '').trim(),
    }));

    const nonEmptyRows = trimmedRows.filter((row) => row.student_email !== '');
    const hasInvalidEmail = nonEmptyRows.some((row) => !row.student_email.includes('@'));

    if (hasInvalidEmail) {
      alert('Please provide valid email addresses for all students.');
      return;
    }

    if (duplicateEmails.size > 0) {
      alert('Duplicate student emails found. Please keep one row per email.');
      return;
    }

    onSave(nonEmptyRows);
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center m-0" style={{ marginTop: 0 }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="roster-editor-title"
        aria-describedby="roster-editor-description"
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b sticky top-0 bg-white">
          <h2 id="roster-editor-title" className="text-2xl font-bold text-indigo-700">Edit Roster: {moduleName}</h2>
          <p id="roster-editor-description" className="text-sm text-gray-500">Academic year start: {year}</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">Students: <span className="font-semibold">{rows.length}</span></div>
            <button
              type="button"
              onClick={addRow}
              disabled={isSaving}
              className="bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 transition disabled:bg-indigo-400"
            >
              Add Student
            </button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 gap-2 bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700">
              <div className="col-span-5">Email</div>
              <div className="col-span-5">Student ID (optional)</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            <div className="max-h-[50vh] overflow-y-auto">
              {rows.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500">No students yet. Click Add Student to begin.</div>
              ) : rows.map((row) => {
                const normalizedEmail = (row.student_email || '').trim().toLowerCase();
                const isDuplicate = normalizedEmail && duplicateEmails.has(normalizedEmail);

                return (
                  <div key={row.localId} className="grid grid-cols-12 gap-2 px-3 py-2 border-t items-center">
                    <div className="col-span-5">
                      <input
                        aria-label="Student email"
                        type="email"
                        value={row.student_email}
                        onChange={(e) => updateRow(row.localId, 'student_email', e.target.value)}
                        disabled={isSaving}
                        className={`w-full p-2 border rounded ${isDuplicate ? 'border-red-400' : 'border-gray-300'}`}
                        placeholder="student@university.ac.uk"
                      />
                      {isDuplicate ? <div className="text-xs text-red-600 mt-1">Duplicate email</div> : null}
                    </div>
                    <div className="col-span-5">
                      <input
                        aria-label="Student ID"
                        type="text"
                        value={row.student_identifier}
                        onChange={(e) => updateRow(row.localId, 'student_identifier', e.target.value)}
                        disabled={isSaving}
                        className="w-full p-2 border border-gray-300 rounded"
                        placeholder="e.g. 22001234"
                      />
                    </div>
                    <div className="col-span-2 text-right">
                      <button
                        type="button"
                        onClick={() => deleteRow(row.localId)}
                        disabled={isSaving}
                        className="text-red-600 hover:text-red-700 font-semibold disabled:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 border-t sticky bottom-0 bg-white flex gap-3">
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="w-1/2 bg-gray-200 text-gray-800 font-semibold py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-1/2 bg-indigo-600 text-white font-semibold py-2 rounded-lg hover:bg-indigo-700 transition disabled:bg-indigo-400"
          >
            {isSaving ? 'Saving Roster...' : 'Save Roster'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RosterEditorModal;
